import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { DocumentKind, PixKeyType } from "@ichegou/contracts";
import { DriverApp, SiteShell } from "./Site.tsx";
import {
  ageOn, digits, displayDay, displayMonth, DOCUMENT_TEXT, emailSuggestion, formatCep, formatCpf, formatDate, formatMonth, formatPhone, formatPlate, isMobile, isoDay, isoMonth,
  isPlate, isValidCpf, isValidEmail, MAX_FILE_BYTES, registrationApi as api, RegistrationError, session, STEP_HINTS, STEPS, type Channel, type City, type Finder, type Profile, type Registration,
} from "./registration.ts";

type View = "loading" | "contact" | "resume" | "code" | "personal" | "address" | "vehicle" | "documents" | "payout" | "review" | "submitted" | "already_driver";
type HeadingRef = React.RefObject<HTMLHeadingElement | null>;
type Errors = Record<string, string | undefined>;

const RESEND_SECONDS = 30;

/** The screen for a step; `phone_code` can't reach the page, the code has opened it by then. */
const viewOf = (r: Registration): View => (r.step === "phone_code" ? "personal" : r.step);
const stepIndex: Partial<Record<View, number>> = { contact: 0, code: 0, personal: 1, address: 2, vehicle: 3, documents: 4, payout: 5, review: 6 };
const PREVIOUS: Partial<Record<View, View>> = { address: "personal", vehicle: "address", documents: "vehicle", payout: "documents", review: "payout" };

const messageOf = (e: unknown) => (e instanceof Error ? e.message : "Algo deu errado do nosso lado. Tente de novo em instantes.");
const codeOf = (e: unknown) => (e instanceof RegistrationError ? e.code : "");
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Seja motorista (ichegoubr.com/motorista/cadastro): contact and the phone's code, then the driver
 * app's cadastro, section by section, through the same API. Each one is saved, so whoever stops comes
 * back with the phone or the CPF and a new code, here or in the app, and the same queue reviews it.
 */
export function RegisterPage({ search = typeof window === "undefined" ? "" : window.location.search }: { search?: string }) {
  const saved = useRef(session.read());
  const [view, setView] = useState<View>(() => (saved.current.token ? "loading" : saved.current.find ? "code" : new URLSearchParams(search).has("continuar") ? "resume" : "contact"));
  const [token, setToken] = useState<string | null>(saved.current.token ?? null);
  const [reg, setReg] = useState<Registration | null>(null);
  const [find, setFind] = useState<Finder | null>(saved.current.find ?? null);
  /** A code asked by phone or CPF for an existing registration: it may not arrive, so the page says so. */
  const [resuming, setResuming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);

  // A new step: the top of the page, and a screen reader hears its title.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    window.scrollTo({ top: 0 });
    heading.current?.focus({ preventScroll: true });
  }, [view]);

  function expired() {
    session.clear();
    setToken(null);
    setReg(null);
    setNotice("Por segurança, confirme seu celular de novo para continuar. O que você preencheu está salvo.");
    setView("resume");
  }

  /** Shows where the cadastro stands; once it's sent, the app takes over and the browser keeps no session. */
  function show(r: Registration, t: string | null = token) {
    setReg(r);
    setView(viewOf(r));
    if (t && (r.step === "submitted" || r.step === "already_driver")) {
      session.clear();
      setToken(null);
      void api.signOut(t).catch(() => undefined);
    }
  }

  useEffect(() => {
    const t = saved.current.token;
    if (!t) return;
    let live = true;
    api.progress(t).then(
      (r) => {
        if (live) show(r, t);
      },
      (e) => {
        if (!live) return;
        if (e instanceof RegistrationError && (e.status === 401 || e.status === 404)) expired();
        else {
          setNotice(messageOf(e));
          setView("resume");
        }
      },
    );
    return () => {
      live = false;
    };
    // Once, with what this tab had saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function codeAsked(next: Finder, isResume: boolean) {
    session.write({ find: next });
    setFind(next);
    setResuming(isResume);
    setNotice(null);
    setView("code");
  }

  function opened(newToken: string, r: Registration) {
    session.write({ token: newToken });
    setToken(newToken);
    show(r, newToken);
  }

  /** Runs a save and moves on to what's next; a session that ended goes back to the code, not to an error. */
  async function save(run: (t: string) => Promise<unknown>) {
    try {
      await run(token!);
      show(await api.progress(token!));
    } catch (e) {
      if (e instanceof RegistrationError && e.status === 401) expired();
      else throw e;
    }
  }

  const index = stepIndex[view];
  const back = PREVIOUS[view];
  const profile = reg?.profile;
  const step = { heading, onBack: back ? () => setView(back) : undefined };

  return (
    <SiteShell current="driver">
      <section className={index === undefined ? "reg" : "reg reg-steps"}>
        {index !== undefined && (
          <div className="reg-side">
            <Progress index={index} />
            {index >= 1 && <p className="reg-saved"><Check />Salvo a cada passo. Se precisar parar, volte com seu celular ou CPF e continue daqui.</p>}
          </div>
        )}
        <div className="reg-card">
          {notice && <p className="reg-notice" role="status">{notice}</p>}
          {view === "loading" && <p className="reg-loading" role="status">Abrindo seu cadastro…</p>}
          {view === "contact" && <ContactStep heading={heading} onSent={(next) => codeAsked(next, false)} onResume={() => setView("resume")} />}
          {view === "resume" && <ResumeStep heading={heading} onSent={(next) => codeAsked(next, true)} onNew={() => { setNotice(null); setView("contact"); }} />}
          {view === "code" && find && (
            <CodeStep
              heading={heading}
              find={find}
              resuming={resuming}
              onOpened={opened}
              onBack={() => { session.clear(); setFind(null); setView(resuming ? "resume" : "contact"); }}
              onNew={() => { session.clear(); setFind(null); setView("contact"); }}
            />
          )}
          {view === "personal" && profile && <PersonalStep {...step} profile={profile} onSkip={() => show(reg!)} onSave={(input) => save((t) => api.personal(t, input))} />}
          {view === "address" && profile && token && <AddressStep {...step} token={token} profile={profile} onSkip={() => show(reg!)} onSave={(input) => save((t) => api.address(t, input))} />}
          {view === "vehicle" && profile && token && <VehicleStep {...step} token={token} profile={profile} onSkip={() => show(reg!)} onSave={(input) => save((t) => api.vehicle(t, input))} />}
          {view === "documents" && reg && token && (
            <DocumentsStep {...step} reg={reg} onUpload={async (kind, file) => {
              const next = await api.document(token, kind, file);
              // Stays on this screen until "Continuar", whatever's still missing.
              setReg({ ...reg, profile: next });
              await api.progress(token).then((r) => setReg((cur) => (cur ? { ...cur, step: r.step, profile: r.profile } : r)), () => undefined);
            }} onContinue={() => show(reg)} />
          )}
          {view === "payout" && reg && <PayoutStep {...step} reg={reg} onSkip={() => show(reg)} onSave={(input) => save((t) => api.payout(t, input))} />}
          {view === "review" && reg && token && (
            <ReviewStep {...step} reg={reg} onEdit={setView} onSubmit={async () => {
              try {
                show(await api.submit(token));
              } catch (e) {
                if (e instanceof RegistrationError && e.status === 401) expired();
                else throw e;
              }
            }} />
          )}
          {view === "submitted" && <Done heading={heading} title={`Recebemos seu cadastro${profile?.personal ? `, ${profile.personal.fullName.split(" ")[0]}` : ""}!`} body={`Nossa equipe vai conferir seus dados e documentos. Acompanhe no app ichegou Motorista, entrando com o celular ${reg?.phone ?? ""}. Aprovado o cadastro, é só seguir o app para ficar online.`} />}
          {view === "already_driver" && <Done heading={heading} title="Você já enviou seu cadastro" body={profile?.status === "resubmission_requested" ? `Nossa equipe pediu alguns ajustes. Entre no app ichegou Motorista com o celular ${reg?.phone ?? ""} para ver o que reenviar.` : `Não precisa preencher nada aqui. Acompanhe tudo no app ichegou Motorista, entrando com o celular ${reg?.phone ?? ""}.`} />}
        </div>
      </section>
    </SiteShell>
  );
}

function Check() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>;
}

/** The bar above the form on a phone; on a large screen the same list is the timeline beside it (site.css). */
function Progress({ index }: { index: number }) {
  return (
    <div className="reg-progress">
      <p className="reg-count" aria-live="polite">Passo {index + 1} de {STEPS.length}<span> · {STEPS[index]}</span></p>
      <ol aria-label="Etapas do cadastro">
        {STEPS.map((label, i) => (
          <li key={label} className={i < index ? "done" : i === index ? "now" : undefined} aria-current={i === index ? "step" : undefined}>
            <span className="reg-mark" aria-hidden="true">{i < index ? <Check /> : i + 1}</span>
            <span className="reg-step"><b>{label}</b><small>{STEP_HINTS[label]}</small></span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Head({ heading, title, children }: { heading: HeadingRef; title: string; children?: ReactNode }) {
  return (
    <header className="reg-head">
      <h1 ref={heading} tabIndex={-1}>{title}</h1>
      {children && <p>{children}</p>}
    </header>
  );
}

function Field({ label, error, hint, children, id }: { label: string; id: string; error?: string | null; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className={`reg-field${error ? " invalid" : ""}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? <p className="reg-error" id={`${id}-error`} role="alert">{error}</p> : hint ? <p className="reg-hint" id={`${id}-hint`}>{hint}</p> : null}
    </div>
  );
}

const describedBy = (id: string, error?: string | null, hint?: unknown) => (error ? `${id}-error` : hint ? `${id}-hint` : undefined);

/** A text input wired to its label, error and hint. */
function TextField({ id, label, value, onChange, error, hint, ...input }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: string; hint?: ReactNode } & Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "value" | "onChange">) {
  return (
    <Field id={id} label={label} error={error} hint={hint}>
      <input id={id} type="text" value={value} aria-invalid={!!error} aria-describedby={describedBy(id, error, hint)} onChange={(e) => onChange(e.target.value)} {...input} />
    </Field>
  );
}

function Actions({ busy, label = "Continuar", onBack, error }: { busy: boolean; label?: string; onBack?: () => void; error?: string }) {
  return (
    <>
      {error && <p className="reg-error" role="alert">{error}</p>}
      <div className="reg-actions">
        {onBack && <button type="button" className="btn btn-ghost reg-back" onClick={onBack}>Voltar</button>}
        <button className="btn btn-primary reg-submit" type="submit" disabled={busy} aria-busy={busy}>{busy ? "Salvando…" : label}</button>
      </div>
    </>
  );
}

/** Field values with errors that clear as soon as the field is corrected. */
function useForm<T extends Record<string, string>>(initial: T) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const started = useRef(JSON.stringify(initial));
  const set = (key: keyof T & string, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined, form: undefined }));
  };
  return { values, set, errors, setErrors, busy, setBusy, changed: () => JSON.stringify(values) !== started.current };
}

/** The saving half of a step: errors the API answers land on their field, the rest under the form. */
async function submitting(form: { setBusy: (b: boolean) => void; setErrors: (e: Errors) => void }, fields: Record<string, string>, run: () => Promise<void>) {
  form.setBusy(true);
  try {
    await run();
  } catch (err) {
    const field = fields[codeOf(err)];
    form.setErrors(field ? { [field]: messageOf(err) } : { form: messageOf(err) });
    form.setBusy(false);
  }
}

function ContactStep({ heading, onSent, onResume }: { heading: HeadingRef; onSent: (find: Finder) => void; onResume: () => void }) {
  const form = useForm({ phone: "", email: "" });
  const { phone, email } = form.values;
  const suggestion = emailSuggestion(email);
  const emailHint = suggestion ? <>Você quis dizer <button type="button" className="reg-link" onClick={() => form.set("email", suggestion)}>{suggestion}</button>?</> : <>Se você parar no meio, lembramos por e-mail de terminar, no máximo duas vezes. <a href="/privacidade" target="_blank" rel="noreferrer">Privacidade</a></>;

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Errors = {};
    if (!isMobile(phone)) next.phone = "Digite o celular com DDD. Ex.: (11) 98765-4321";
    if (!isValidEmail(email)) next.email = "Confira o e-mail: ele parece incompleto.";
    form.setErrors(next);
    if (next.phone || next.email) return;
    const phoneNumber = `+55${digits(phone)}`;
    void submitting(form, { invalid_phone: "phone", invalid_email: "email" }, async () => {
      await api.start(phoneNumber, email.trim());
      onSent({ phoneNumber });
    });
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Seja motorista ichegou">Comece pelo seu contato. Tenha em mãos a CNH e o documento do veículo; salvamos cada passo.</Head>
      <TextField id="phone" label="Celular" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="(11) 98765-4321" value={phone} onChange={(v) => form.set("phone", formatPhone(v))} error={form.errors.phone} hint="Enviamos um código por SMS para confirmar que é você." />
      <TextField id="email" label="E-mail" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="voce@exemplo.com" value={email} onChange={(v) => form.set("email", v)} error={form.errors.email} hint={emailHint} />
      <Actions busy={form.busy} error={form.errors.form} />
      <p className="reg-alt">Já começou? <button type="button" className="reg-link" onClick={onResume}>Continuar meu cadastro</button></p>
    </form>
  );
}

function ResumeStep({ heading, onSent, onNew }: { heading: HeadingRef; onSent: (find: Finder) => void; onNew: () => void }) {
  const [by, setBy] = useState<"phone" | "cpf">("phone");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const isPhone = by === "phone";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (isPhone ? !isMobile(value) : !isValidCpf(value)) {
      setError(isPhone ? "Digite o celular com DDD. Ex.: (11) 98765-4321" : "Confira o CPF: os números não batem.");
      return;
    }
    setError(undefined);
    setBusy(true);
    const find: Finder = isPhone ? { phoneNumber: `+55${digits(value)}` } : { cpf: digits(value) };
    try {
      await api.resume(find);
      onSent(find);
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Continue seu cadastro">Informe o celular ou o CPF que você usou. Enviamos um código para o celular do cadastro.</Head>
      <Pills legend="Como encontrar seu cadastro" name="by" value={by} options={[["phone", "Celular"], ["cpf", "CPF"]]} onChange={(next) => { setBy(next); setValue(""); setError(undefined); }} />
      <TextField key={by} id="find" label={isPhone ? "Celular" : "CPF"} type={isPhone ? "tel" : "text"} inputMode="numeric" autoComplete={isPhone ? "tel-national" : "off"} placeholder={isPhone ? "(11) 98765-4321" : "000.000.000-00"} value={value} onChange={(v) => { setValue(isPhone ? formatPhone(v) : formatCpf(v)); setError(undefined); }} error={error} />
      <Actions busy={busy} label="Enviar código" />
      <p className="reg-alt">Ainda não começou? <button type="button" className="reg-link" onClick={onNew}>Começar meu cadastro</button></p>
    </form>
  );
}

function Pills<T extends string>({ legend, name, value, options, onChange, error }: { legend: string; name: string; value: T | ""; options: [T, string][]; onChange: (v: T) => void; error?: string }) {
  return (
    <div className={`reg-field${error ? " invalid" : ""}`}>
      <span className="reg-legend" id={`${name}-legend`}>{legend}</span>
      <div className="reg-tabs" role="radiogroup" aria-labelledby={`${name}-legend`} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
        {options.map(([option, label]) => (
          <label key={option} className={value === option ? "on" : undefined}>
            <input type="radio" name={name} checked={value === option} onChange={() => onChange(option)} />
            {label}
          </label>
        ))}
      </div>
      {error && <p className="reg-error" role="alert">{error}</p>}
    </div>
  );
}

function useCountdown(initial: number) {
  const [left, setLeft] = useState(initial);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, setLeft] as const;
}

function CodeStep({ heading, find, resuming, onOpened, onBack, onNew }: { heading: HeadingRef; find: Finder; resuming: boolean; onOpened: (token: string, r: Registration) => void; onBack: () => void; onNew: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useCountdown(RESEND_SECONDS);
  const input = useRef<HTMLInputElement>(null);
  const phone = "phoneNumber" in find ? formatPhone(find.phoneNumber) : null;
  useEffect(() => input.current?.focus({ preventScroll: true }), []);

  async function verify(value: string) {
    if (value.length !== 6) {
      setError("O código tem 6 números.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { token, registration } = await api.verify(find, value);
      onOpened(token, registration);
    } catch (err) {
      setError(messageOf(err));
      setCode("");
      setBusy(false);
    }
  }

  async function resend(channel: Channel) {
    setError(null);
    setInfo(null);
    try {
      await api.resume(find, channel);
      setLeft(RESEND_SECONDS);
      setInfo(channel === "whatsapp" ? "Enviamos um novo código pelo WhatsApp." : "Enviamos um novo código por SMS.");
    } catch (err) {
      setError(messageOf(err));
    }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void verify(code); }} noValidate>
      <Head heading={heading} title="Digite o código">
        {resuming ? `Se houver um cadastro com ${phone ? "este celular" : "este CPF"}, o código chega em instantes no celular dele.` : <>Enviamos 6 números por SMS para <b>{phone}</b>.</>}
      </Head>
      <Field id="code" label="Código" error={error}>
        <input
          ref={input} id="code" className="reg-code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} placeholder="000000" value={code} disabled={busy}
          aria-invalid={!!error} aria-describedby={describedBy("code", error)}
          onChange={(e) => {
            const next = digits(e.target.value).slice(0, 6);
            setCode(next);
            if (next.length === 6) void verify(next);
          }}
        />
      </Field>
      {info && <p className="reg-info" role="status">{info}</p>}
      <Actions busy={busy} label="Confirmar" />
      <div className="reg-resend">
        {left > 0 ? <span>Reenviar em {left} s</span> : (
          <>
            <span>Não chegou?</span>
            <button type="button" className="reg-link" onClick={() => void resend("sms")}>Reenviar por SMS</button>
            <button type="button" className="reg-link" onClick={() => void resend("whatsapp")}>Enviar pelo WhatsApp</button>
          </>
        )}
      </div>
      <p className="reg-alt">
        <button type="button" className="reg-link" onClick={onBack}>{resuming ? "Usar outro celular ou CPF" : "Trocar o celular"}</button>
        {resuming && <> · <button type="button" className="reg-link" onClick={onNew}>Começar um novo cadastro</button></>}
      </p>
    </form>
  );
}

type StepProps = { heading: HeadingRef; onBack?: () => void; onSkip: () => void };

function PersonalStep({ heading, onBack, onSkip, profile, onSave }: StepProps & { profile: Profile; onSave: (input: Parameters<typeof api.personal>[1]) => Promise<void> }) {
  const saved = profile.personal;
  const form = useForm({
    fullName: saved?.fullName ?? "", cpf: "", birth: saved ? displayDay(saved.birthDate) : "", cnhNumber: saved?.cnhNumber ?? "",
    expiry: saved ? displayMonth(saved.cnhExpiresAt) : "", category: saved?.cnhCategory ?? "", ear: saved?.cnhHasEar ? "yes" : "", gender: saved?.gender ?? "",
  });
  const v = form.values;

  function submit(e: FormEvent) {
    e.preventDefault();
    // Nothing touched on the way back through: the CPF only comes masked, so there's nothing to resend.
    if (saved && !form.changed()) return onSkip();
    const next: Errors = {};
    if (v.fullName.trim().length < 5 || v.fullName.trim().split(/\s+/).length < 2) next.fullName = "Digite o nome completo, como na CNH";
    if (!v.cpf && saved) next.cpf = "Digite o CPF de novo para salvar as alterações.";
    else if (!isValidCpf(v.cpf)) next.cpf = "Confira o CPF: os números não batem.";
    const birthDate = isoDay(v.birth);
    if (!birthDate) next.birth = "Use o formato DD/MM/AAAA";
    else if (ageOn(birthDate, today()) > 100) next.birth = "Data de nascimento inválida";
    else if (ageOn(birthDate, today()) < 21) next.birth = "Para dirigir no ichegou é preciso ter 21 anos ou mais";
    if (digits(v.cnhNumber).length !== 11) next.cnhNumber = "O registro tem 11 dígitos";
    const cnhExpiresAt = isoMonth(v.expiry);
    if (!cnhExpiresAt) next.expiry = "Use o formato MM/AAAA";
    else if (cnhExpiresAt < today().slice(0, 7)) next.expiry = "Sua CNH está vencida";
    if (!v.category) next.category = "Escolha a categoria";
    if (!v.ear) next.ear = "A CNH precisa ter a observação EAR (Exerce Atividade Remunerada). Sem ela não conseguimos aprovar o cadastro.";
    form.setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    void submitting(form, { invalid_cpf: "cpf", cpf_taken: "cpf", too_young: "birth", invalid_birth_date: "birth", cnh_expired: "expiry" }, () => onSave({
      fullName: v.fullName.trim(), cpf: digits(v.cpf), birthDate: birthDate!, cnhNumber: digits(v.cnhNumber), cnhCategory: v.category as "A", cnhExpiresAt: cnhExpiresAt!,
      cnhHasEar: true, gender: v.gender ? (v.gender as "female") : null,
    }));
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Seus dados">Use os mesmos dados da sua CNH.</Head>
      <TextField id="fullName" label="Nome completo" autoComplete="name" autoCapitalize="words" placeholder="Como está na CNH" value={v.fullName} onChange={(x) => form.set("fullName", x)} error={form.errors.fullName} />
      <div className="reg-row">
        <TextField id="cpf" label="CPF" inputMode="numeric" autoComplete="off" placeholder={saved?.cpfMasked ?? "000.000.000-00"} value={v.cpf} onChange={(x) => form.set("cpf", formatCpf(x))} error={form.errors.cpf} />
        <TextField id="birth" label="Nascimento" inputMode="numeric" autoComplete="bday" placeholder="DD/MM/AAAA" value={v.birth} onChange={(x) => form.set("birth", formatDate(x))} error={form.errors.birth} />
      </div>
      <div className="reg-row">
        <TextField id="cnhNumber" label="Nº de registro da CNH" inputMode="numeric" autoComplete="off" placeholder="11 dígitos" maxLength={11} value={v.cnhNumber} onChange={(x) => form.set("cnhNumber", digits(x))} error={form.errors.cnhNumber} />
        <TextField id="expiry" label="Validade da CNH" inputMode="numeric" autoComplete="off" placeholder="MM/AAAA" value={v.expiry} onChange={(x) => form.set("expiry", formatMonth(x))} error={form.errors.expiry} />
      </div>
      <Pills legend="Categoria da CNH" name="category" value={v.category as "A"} options={[["A", "A"], ["B", "B"], ["AB", "AB"], ["C", "C"], ["D", "D"]]} onChange={(x) => form.set("category", x)} error={form.errors.category} />
      <label className={`reg-check${form.errors.ear ? " invalid" : ""}`}>
        <input type="checkbox" checked={!!v.ear} onChange={(e) => form.set("ear", e.target.checked ? "yes" : "")} />
        <span>Minha CNH tem “EAR”<small>Exerce Atividade Remunerada, obrigatório por lei</small></span>
      </label>
      {form.errors.ear && <p className="reg-error" role="alert">{form.errors.ear}</p>}
      <Field id="gender" label="Gênero (opcional)" hint="Nossa equipe confere na CNH. Libera tipos de corrida limitados por gênero.">
        <select id="gender" value={v.gender} aria-describedby="gender-hint" onChange={(e) => form.set("gender", e.target.value)}>
          <option value="">Prefiro não dizer</option>
          <option value="female">Feminino</option>
          <option value="male">Masculino</option>
        </select>
      </Field>
      <Actions busy={form.busy} onBack={onBack} error={form.errors.form} />
    </form>
  );
}

function AddressStep({ heading, onBack, onSkip, token, profile, onSave }: StepProps & { token: string; profile: Profile; onSave: (input: Parameters<typeof api.address>[1]) => Promise<void> }) {
  const saved = profile.address;
  const form = useForm({ cityId: saved?.cityId ?? "", cep: saved?.cep ?? "", line: saved?.line ?? "", number: saved?.number ?? "", complement: saved?.complement ?? "" });
  const v = form.values;
  const [cities, setCities] = useState<City[]>([]);
  const [lookup, setLookup] = useState<"idle" | "loading" | "found" | "missing">(saved ? "found" : "idle");
  const typed = useRef(saved?.cep ?? "");

  useEffect(() => {
    let live = true;
    api.cities(token).then((list) => {
      if (!live) return;
      setCities(list);
      if (list.length === 1) form.set("cityId", list[0]!.id);
    }, (e) => live && form.setErrors({ form: messageOf(e) }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function cepChanged(value: string) {
    const cep = formatCep(value);
    form.set("cep", cep);
    typed.current = cep;
    if (digits(cep).length !== 8) {
      setLookup("idle");
      return;
    }
    setLookup("loading");
    api.postalCode(token, digits(cep)).then(
      (found) => {
        if (typed.current !== cep) return;
        form.set("line", [found.street, found.neighborhood].filter(Boolean).join(" · "));
        setLookup("found");
      },
      () => typed.current === cep && setLookup("missing"),
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (saved && !form.changed()) return onSkip();
    const next: Errors = {};
    if (!v.cityId) next.cityId = "Escolha a cidade onde vai dirigir";
    if (digits(v.cep).length !== 8) next.cep = "Digite os 8 dígitos do CEP";
    if (v.line.trim().length < 3) next.line = "Digite a rua e o bairro";
    if (!v.number.trim()) next.number = "Digite o número";
    form.setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    void submitting(form, { invalid_city: "cityId" }, () => onSave({ cityId: v.cityId, cep: v.cep, line: v.line.trim(), number: v.number.trim(), complement: v.complement.trim() || null }));
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Onde você mora e vai dirigir" />
      <Field id="cityId" label="Cidade onde vai dirigir" error={form.errors.cityId}>
        <select id="cityId" value={v.cityId} aria-invalid={!!form.errors.cityId} aria-describedby={describedBy("cityId", form.errors.cityId)} onChange={(e) => form.set("cityId", e.target.value)}>
          <option value="" disabled>Escolha a cidade</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.state}</option>)}
        </select>
      </Field>
      <div className="reg-row">
        <TextField id="cep" label="CEP residencial" inputMode="numeric" autoComplete="postal-code" placeholder="00000-000" value={v.cep} onChange={cepChanged} error={form.errors.cep} hint={lookup === "loading" ? "Buscando o endereço…" : lookup === "missing" ? "Não achamos esse CEP. Preencha a rua abaixo." : undefined} />
        <TextField id="number" label="Número" inputMode="text" autoComplete="off" placeholder="Nº" value={v.number} onChange={(x) => form.set("number", x)} error={form.errors.number} />
      </div>
      <TextField id="line" label="Endereço" autoComplete="street-address" placeholder="Rua · Bairro" value={v.line} onChange={(x) => form.set("line", x)} error={form.errors.line} hint={lookup === "found" ? "Preenchido pelo CEP. Corrija se precisar." : undefined} />
      <TextField id="complement" label="Complemento (opcional)" autoComplete="off" placeholder="Apto, bloco" value={v.complement} onChange={(x) => form.set("complement", x)} />
      <Actions busy={form.busy} onBack={onBack} error={form.errors.form} />
    </form>
  );
}

function VehicleStep({ heading, onBack, onSkip, token, profile, onSave }: StepProps & { token: string; profile: Profile; onSave: (input: Parameters<typeof api.vehicle>[1]) => Promise<void> }) {
  const saved = profile.vehicle;
  const form = useForm({ plate: saved?.plate ?? "", renavam: "", year: saved ? String(saved.year) : "", make: saved?.make ?? "", model: saved?.model ?? "", color: saved?.color ?? "", moto: saved?.isMotorcycle ? "yes" : "" });
  const v = form.values;
  const [lookup, setLookup] = useState<"idle" | "loading" | "found" | "missing">(saved ? "found" : "idle");
  const typed = useRef(saved?.plate ?? "");

  function plateChanged(value: string) {
    const plate = formatPlate(value);
    form.set("plate", plate);
    typed.current = plate;
    if (!isPlate(plate)) {
      setLookup("idle");
      return;
    }
    setLookup("loading");
    api.plate(token, plate).then(
      (car) => {
        if (typed.current !== plate) return;
        form.set("make", car.make);
        form.set("model", car.model);
        form.set("color", car.color);
        form.set("moto", car.isMotorcycle ? "yes" : "");
        if (car.year) form.set("year", String(car.year));
        setLookup("found");
      },
      () => typed.current === plate && setLookup("missing"),
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (saved && !form.changed()) return onSkip();
    const next: Errors = {};
    if (!isPlate(v.plate)) next.plate = "Placa inválida. Ex.: ABC1D23";
    const renavam = digits(v.renavam);
    if (!renavam && saved) next.renavam = "Digite o RENAVAM de novo para salvar as alterações.";
    else if (renavam.length < 9 || renavam.length > 11) next.renavam = "O RENAVAM tem de 9 a 11 dígitos";
    const year = Number(v.year);
    if (!/^\d{4}$/.test(v.year) || year < 1990 || year > Number(today().slice(0, 4)) + 1) next.year = "Digite o ano do veículo";
    if (v.make.trim().length < 2) next.make = "Digite a marca";
    if (!v.model.trim()) next.model = "Digite o modelo";
    if (v.color.trim().length < 3) next.color = "Digite a cor";
    form.setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    void submitting(form, { plate_taken: "plate", vehicle_too_old: "year", invalid_year: "year" }, () => onSave({ plate: v.plate, renavam, year, make: v.make.trim(), model: v.model.trim(), color: v.color.trim(), isMotorcycle: !!v.moto }));
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Seu veículo">Os tipos de corrida liberados dependem do ano e do modelo.</Head>
      <div className="reg-row">
        <TextField id="plate" label="Placa" autoCapitalize="characters" autoComplete="off" spellCheck={false} placeholder="ABC1D23" value={v.plate} onChange={plateChanged} error={form.errors.plate} hint={lookup === "loading" ? "Buscando pela placa…" : lookup === "found" && !saved ? "Encontramos pela placa. Corrija se precisar." : undefined} />
        <TextField id="year" label="Ano" inputMode="numeric" autoComplete="off" placeholder="AAAA" maxLength={4} value={v.year} onChange={(x) => form.set("year", digits(x))} error={form.errors.year} />
      </div>
      <TextField id="renavam" label="RENAVAM" inputMode="numeric" autoComplete="off" placeholder={saved ? "Salvo · digite para alterar" : "9 a 11 dígitos"} maxLength={11} value={v.renavam} onChange={(x) => form.set("renavam", digits(x))} error={form.errors.renavam} />
      <div className="reg-row">
        <TextField id="make" label="Marca" autoCapitalize="words" autoComplete="off" placeholder="Chevrolet" value={v.make} onChange={(x) => form.set("make", x)} error={form.errors.make} />
        <TextField id="model" label="Modelo" autoCapitalize="words" autoComplete="off" placeholder="Onix Plus" value={v.model} onChange={(x) => form.set("model", x)} error={form.errors.model} />
      </div>
      <TextField id="color" label="Cor" autoCapitalize="words" autoComplete="off" placeholder="Prata" value={v.color} onChange={(x) => form.set("color", x)} error={form.errors.color} />
      <label className="reg-check">
        <input type="checkbox" checked={!!v.moto} onChange={(e) => form.set("moto", e.target.checked ? "yes" : "")} />
        <span>É uma moto</span>
      </label>
      <Actions busy={form.busy} onBack={onBack} error={form.errors.form} />
    </form>
  );
}

const DOCUMENT_STATUS: Record<string, { label: string; tone?: string }> = {
  pending: { label: "Enviado", tone: "ok" },
  approved: { label: "Aprovado", tone: "ok" },
  rejected: { label: "Reenvie" },
  expired: { label: "Vencido" },
};

function DocumentsStep({ heading, onBack, reg, onUpload, onContinue }: { heading: HeadingRef; onBack?: () => void; reg: Registration; onUpload: (kind: DocumentKind, file: File) => Promise<void>; onContinue: () => void }) {
  const [uploading, setUploading] = useState<Set<DocumentKind>>(new Set());
  const [errors, setErrors] = useState<Partial<Record<DocumentKind, string>>>({});
  const [form, setForm] = useState<string>();
  const { profile } = reg;

  async function pick(kind: DocumentKind, file: File | undefined) {
    if (!file) return;
    setForm(undefined);
    if (file.size > MAX_FILE_BYTES) {
      setErrors((e) => ({ ...e, [kind]: "O arquivo passa de 10 MB. Envie uma foto menor." }));
      return;
    }
    setErrors((e) => ({ ...e, [kind]: undefined }));
    setUploading((u) => new Set(u).add(kind));
    try {
      await onUpload(kind, file);
    } catch (err) {
      setErrors((e) => ({ ...e, [kind]: messageOf(err) }));
    }
    setUploading((u) => {
      const next = new Set(u);
      next.delete(kind);
      return next;
    });
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (profile.missingDocuments.length) setForm(`Falta enviar: ${profile.missingDocuments.map((k) => DOCUMENT_TEXT[k].title).join(", ")}.`);
    else onContinue();
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Seus documentos">Foto (JPEG, PNG ou HEIC) ou PDF, até 10 MB cada. No celular, você pode tirar a foto na hora.</Head>
      <ul className="reg-docs">
        {reg.requiredDocuments.map((kind) => {
          const text = DOCUMENT_TEXT[kind];
          const doc = profile.documents.find((d) => d.kind === kind);
          const status = doc && DOCUMENT_STATUS[doc.status];
          const busy = uploading.has(kind);
          const error = errors[kind] ?? (doc?.status === "rejected" ? doc.rejectionReason ?? undefined : undefined);
          return (
            <li key={kind} className={error ? "invalid" : undefined}>
              <div className="reg-doc-text">
                <b>{text.title}{status && !busy && <span className={`reg-tag${status.tone ? ` ${status.tone}` : ""}`}>{status.label}</span>}</b>
                <span>{text.hint}</span>
                {error && <p className="reg-error" role="alert">{error}</p>}
              </div>
              <label className={`btn btn-ghost reg-upload${busy ? " busy" : ""}`}>
                <input type="file" accept={text.accept} disabled={busy} aria-label={`Enviar ${text.title}`} data-kind={kind} onChange={(e) => { void pick(kind, e.target.files?.[0]); e.target.value = ""; }} />
                {busy ? "Enviando…" : doc ? "Trocar" : "Enviar"}
              </label>
            </li>
          );
        })}
      </ul>
      <Actions busy={uploading.size > 0} onBack={onBack} error={form} />
    </form>
  );
}

const PIX_TYPES: [PixKeyType, string][] = [["cpf", "CPF"], ["phone", "Celular"], ["email", "E-mail"], ["random", "Aleatória"]];
const PIX_PLACEHOLDER: Record<PixKeyType, string> = { cpf: "000.000.000-00", phone: "(11) 98765-4321", email: "voce@exemplo.com", random: "Chave aleatória" };

function PayoutStep({ heading, onBack, onSkip, reg, onSave }: StepProps & { reg: Registration; onSave: (input: Parameters<typeof api.payout>[1]) => Promise<void> }) {
  const saved = reg.profile.payout;
  const [type, setType] = useState<PixKeyType>(saved?.pixKeyType ?? "cpf");
  const form = useForm({ key: "" });
  const [terms, setTerms] = useState([false, false, false]);
  const background = reg.requiredDocuments.includes("background_state");
  const texts: ReactNode[] = [
    <>Li e aceito os <a href="/termos#motoristas" target="_blank" rel="noreferrer">Termos do Motorista Parceiro</a></>,
    <>Concordo com o <a href="/termos#conduta" target="_blank" rel="noreferrer">Código de Conduta ichegou</a></>,
    <>{background ? "Autorizo a consulta de antecedentes e da CNH" : "Autorizo a consulta da CNH"} (<a href="/privacidade" target="_blank" rel="noreferrer">LGPD</a>)</>,
  ];
  const key = form.values.key;
  const format = (x: string) => (type === "cpf" ? formatCpf(x) : type === "phone" ? formatPhone(x) : x);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (saved && !key && reg.profile.termsAccepted) return onSkip();
    const next: Errors = {};
    if (key.trim().length < 3) next.key = saved ? `Salvo: ${saved.pixKeyMasked}. Digite a chave de novo para salvar.` : "Digite sua chave Pix";
    if (!terms.every(Boolean)) next.terms = "Aceite os três termos para continuar.";
    form.setErrors(next);
    if (next.key || next.terms) return;
    void submitting(form, { invalid_pix_key: "key", pix_key_not_owner: "key" }, () => onSave({ pixKeyType: type, pixKey: type === "cpf" ? digits(key) : key.trim(), acceptTerms: true }));
  }

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Onde você recebe">Seus ganhos caem por Pix. A chave precisa estar no seu CPF.</Head>
      <Pills legend="Tipo de chave" name="pixType" value={type} options={PIX_TYPES} onChange={(next) => { setType(next); form.set("key", ""); }} />
      <TextField key={type} id="key" label="Chave Pix" inputMode={type === "cpf" || type === "phone" ? "numeric" : "text"} autoCapitalize="none" autoComplete="off" spellCheck={false} placeholder={saved?.pixKeyType === type ? saved.pixKeyMasked : PIX_PLACEHOLDER[type]} value={key} onChange={(x) => form.set("key", format(x))} error={form.errors.key} />
      <div className="reg-terms">
        {texts.map((text, i) => (
          <label key={i} className="reg-check">
            <input type="checkbox" checked={terms[i]} onChange={(e) => { setTerms(terms.map((t, j) => (j === i ? e.target.checked : t))); form.setErrors({ ...form.errors, terms: undefined }); }} />
            <span>{text}</span>
          </label>
        ))}
      </div>
      {form.errors.terms && <p className="reg-error" role="alert">{form.errors.terms}</p>}
      <Actions busy={form.busy} onBack={onBack} error={form.errors.form} />
    </form>
  );
}

function ReviewStep({ heading, onBack, reg, onEdit, onSubmit }: { heading: HeadingRef; onBack?: () => void; reg: Registration; onEdit: (view: View) => void; onSubmit: () => Promise<void> }) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const { personal, address, vehicle, payout, documents } = reg.profile;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await onSubmit();
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  }

  const Group = ({ title, to, children }: { title: string; to: View; children: ReactNode }) => (
    <div>
      <dt>{title}</dt>
      <dd>{children}</dd>
      <button type="button" className="reg-link" onClick={() => onEdit(to)} aria-label={`Editar ${title.toLowerCase()}`}>Editar</button>
    </div>
  );

  return (
    <form onSubmit={submit} noValidate>
      <Head heading={heading} title="Está tudo certo?">Depois de enviar, o cadastro fica em análise e só muda se nossa equipe pedir algum ajuste.</Head>
      <dl className="reg-summary">
        <Group title="Dados" to="personal">{personal?.fullName}<br /><small>CPF {personal?.cpfMasked} · CNH {personal?.cnhCategory}, válida até {personal ? displayMonth(personal.cnhExpiresAt) : ""}</small></Group>
        <Group title="Endereço" to="address">{address?.line}, {address?.number}{address?.complement ? `, ${address.complement}` : ""}<br /><small>CEP {address?.cep}</small></Group>
        <Group title="Veículo" to="vehicle">{vehicle?.make} {vehicle?.model} · {vehicle?.color}<br /><small>{vehicle?.plate} · {vehicle?.year}</small></Group>
        <Group title="Documentos" to="documents">{documents.length} {documents.length === 1 ? "enviado" : "enviados"}</Group>
        <Group title="Recebimento" to="payout">Pix {payout?.pixKeyMasked}</Group>
        <div><dt>Contato</dt><dd>{reg.phone}<br /><small>{reg.email}</small></dd></div>
      </dl>
      <Actions busy={busy} label="Enviar para análise" onBack={onBack} error={error} />
    </form>
  );
}

function Done({ heading, title, body }: { heading: HeadingRef; title: string; body: string }) {
  return (
    <div className="reg-done">
      <span className="reg-done-mark"><Check /></span>
      <Head heading={heading} title={title}>{body}</Head>
      <div className="apps"><DriverApp /></div>
    </div>
  );
}

/** The link in the follow-up emails. A button, so a mail scanner opening the link changes nothing. */
export function UnsubscribePage({ search = typeof window === "undefined" ? "" : window.location.search }: { search?: string }) {
  const token = new URLSearchParams(search).get("t");
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");

  async function stop() {
    if (!token) return;
    setState("busy");
    try {
      await api.unsubscribe(token);
      setState("done");
    } catch {
      setState("error");
    }
  }

  return (
    <SiteShell current="driver">
      <section className="reg">
        <div className="reg-card">
          <header className="reg-head">
            <h1>{state === "done" ? "Pronto, não enviamos mais lembretes" : "Parar os lembretes do cadastro?"}</h1>
            <p>{state === "done" ? "Seu cadastro continua salvo se quiser voltar." : !token ? "Este link está incompleto. Abra de novo pelo e-mail que recebeu." : "Você não vai mais receber e-mails sobre terminar seu cadastro de motorista."}</p>
          </header>
          {state !== "done" && token && <button className="btn btn-primary reg-submit" type="button" disabled={state === "busy"} onClick={() => void stop()}>{state === "busy" ? "Salvando…" : "Parar lembretes"}</button>}
          {state === "error" && <p className="reg-error" role="alert">Não conseguimos agora. Tente de novo em instantes.</p>}
          {state === "done" && <a className="btn btn-ghost reg-submit" href="/motorista/cadastro?continuar=1">Continuar meu cadastro</a>}
        </div>
      </section>
    </SiteShell>
  );
}
